import { ScanType, SellingMode } from "../enums/orderStatus";

export function normalizeSellingMode(raw?: string | null): string {
  return (raw ?? "").trim().toUpperCase();
}

/**
 * Decide UX after `scanOrderItem` using both {@link ScanType} and {@link SellingMode}.
 *
 * Precedence:
 * 1. {@link ScanType.ITEM_ADDED} always refetches (definitive success).
 * 2. Measurement path if type or selling mode says measured (mode can reinforce a mistyped type).
 * 3. Quantity path if type or mode says unit-based.
 * 4. {@link SellingMode.SINGLE_ITEM} with no modal requirement refetches (fallback when type omitted).
 */
export type PostScanFlow =
  | { action: "refetch" }
  | { action: "quantity_modal" }
  | { action: "measurement_modal" }
  | { action: "noop" };

export function resolvePostScanFlow(scan: {
  type?: string;
  sellingMode?: string;
}): PostScanFlow {
  const mode = normalizeSellingMode(scan.sellingMode);
  const isItemAdded = scan.type === ScanType.ITEM_ADDED;

  const needsMeasurement =
    !isItemAdded &&
    (scan.type === ScanType.MEASUREMENT_REQUIRED ||
      mode === SellingMode.MEASURED);

  const needsQuantity =
    !isItemAdded &&
    !needsMeasurement &&
    (scan.type === ScanType.QUANTITY_REQUIRED ||
      mode === SellingMode.UNIT_BASED);

  if (
    isItemAdded ||
    (!needsMeasurement && !needsQuantity && mode === SellingMode.SINGLE_ITEM)
  ) {
    return { action: "refetch" };
  }
  if (needsMeasurement) {
    return { action: "measurement_modal" };
  }
  if (needsQuantity) {
    return { action: "quantity_modal" };
  }
  return { action: "noop" };
}
